import React, { FC, useEffect, useState } from 'react';
import useLocales from 'hooks/useLocales';
import { SubscribtionPlansService } from 'services/subscribtionPlans/subscribtionPlans';
import Button from 'components/Button/Index';
import { useSnackbar } from 'notistack';

import './Index.scss';
import { IMe } from 'core/interface/me';
import { useSelector } from 'react-redux';
import { CompanyService } from 'services/companes/companies';
import { RootState } from 'redux/store';
import { CircularProgress } from '@mui/material';
import { getDateFormatter } from 'helpers/dateConverter';

const RecruitMembership: FC = (): JSX.Element => {
  const { t } = useLocales();
  const me = useSelector((state: IMe) => state.user).user;
  const { companies } = useSelector((state: RootState) => state.company);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const currentPlan = String(companies?.subscription?.plan || 'Free');
  const isFreeUser = currentPlan === 'Free';

  // Helper function to format Stripe timestamps (Unix timestamps in seconds)
  const formatStripeTimestamp = (timestamp: number | null): string => {
    if (!timestamp) return '';
    // Convert Unix timestamp (seconds) to milliseconds for JavaScript Date
    return getDateFormatter(new Date(timestamp * 1000), 'MMM DD, YYYY');
  };

  useEffect(() => {
    CompanyService.getCompanies(me?.company);
  }, [me?.company]);

  const handleGetPlanData = async () => {
    try {
      await SubscribtionPlansService.getPlansData();
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    handleGetPlanData();
  }, []);

  const handleManageSubscription = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await SubscribtionPlansService.getSubscriptionPortal();

      if (response?.url) {
        window.location.href = response.url;
      } else {
        enqueueSnackbar(
          t('Failed to access subscription portal. Please try again.'),
          {
            variant: 'error'
          }
        );
      }
    } catch (error) {
      console.error('Error accessing subscription portal:', error);
      enqueueSnackbar(
        t('Failed to access subscription portal. Please try again.'),
        {
          variant: 'error'
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {companies?.subscription?.plan ? (
        <div className="container">
          <div className="title">{t('My membership')}</div>
          <div className="info_section">
            <div className="info_item">
              <div className="left_side">{t('Current plan')}</div>
              <div className="right_side">
                {String(companies?.subscription?.plan || 'Free')}
              </div>
            </div>

            {/* Current period line - only show if we have subscription data with start and end */}
            {companies?.subscription?.start && companies?.subscription?.end && (
              <div className="info_item">
                <div className="left_side">{t('Current period')}</div>
                <div className="right_side">
                  {formatStripeTimestamp(companies.subscription.start)} - {formatStripeTimestamp(companies.subscription.end)}
                </div>
              </div>
            )}

            {/* Cancellation notice - only show if cancel_at is not null */}
            {companies?.subscription?.cancel_at && (
              <div className="cancellation_notice">
                ({t('Your subscription will be canceled on')} {formatStripeTimestamp(companies.subscription.cancel_at)}, {t('upon cancellation, your account will transition to the Free plan')})
              </div>
            )}

            <div className="info_item">
              <div className="left_side">{t('Can unlock')}</div>
              <div className="right_side">
                {String(
                  companies?.subscription?.current_limit?.candidates || 0
                ) +
                  ' ' +
                  t('candidates')}
              </div>
            </div>

            <div className="info_item">
              <div className="left_side">{t('Can create')}</div>
              <div className="right_side">
                {String(companies?.subscription?.current_limit?.jobs || 0) +
                  ' ' +
                  t('jobs')}
              </div>
            </div>

            <div className="info_item">
              <div className="left_side">{t('Can use')}</div>
              <div className="right_side">
                {String(
                  companies?.subscription?.current_limit?.ai_credits || 0
                ) +
                  ' ' +
                  t('AI credits')}
              </div>
            </div>
          </div>

          {!isFreeUser && (
            <div className="manage_subscription_section">
              <Button
                className="manage-subscription-button"
                t={t}
                title={t('Manage My Subscription')}
                onClick={handleManageSubscription}
                disabled={isLoading}
              />
              <p className="manage-subscription-disclaimer">{t('If you upgrade, downgrade, or cancel your subscription, the change will take effect at the end of your current billing period. If you cancel, your account will automatically go back to the free plan.')}</p>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}
        >
          <CircularProgress />
        </div>
      )}
    </>
  );
};

export default RecruitMembership;
